import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Router } from '@angular/router';
import { DataLayerService } from 'core/data-layer-service/data-layer.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { DebounceService } from '../../../shared/debounce.service';
import { ProductValidationService } from '../services/product-validation-service/product-validation.service';
import { ProductsService } from '../services/products-service/products.service';

@Component({
  selector: 'app-search-product',
  templateUrl: './search-product.component.html',
  styleUrls: ['./search-product.component.scss'],
  standalone: false
})
export class SearchProductComponent implements OnInit {

  formFields = {
    searchField: [''],
  };
  searchForm = this.fb.group(this.formFields);
  appStrings: AppLocalization;
  displayFoundProducts: any[];
  foundProducts: any[];
  isLoading: boolean;
  minCharactersError: boolean;
  hasResults: boolean;
  @ViewChild(MatAutocompleteTrigger) private autocompleteTrigger: MatAutocompleteTrigger;
  @ViewChild('searchInput') private searchInput: ElementRef<HTMLInputElement>;
  resultsNumber = 12;
  regex = /^(\s|\S)*(\S)+(\s|\S)*$/;

  constructor(
    private fb: FormBuilder,
    private appInfoService: AppInfoService,
    private router: Router,
    private productsService: ProductsService,
    private debounceService: DebounceService,
    private dataLayerService: DataLayerService,
    private productValidationService: ProductValidationService
  ) { }

  get isValidatingProduct(): boolean {
    return this.productValidationService.isValidating;
  }

  ngOnInit() {
    this.appStrings = this.appInfoService.appStrings;
    this.minCharactersError = true;
    this.displayFoundProducts = [];
  }

  searchAutocomplete() {
    const searchText = this.searchForm.controls.searchField.value;

    if (!searchText || searchText.length <= 2) {
      this.minCharactersError = true;
      this.displayFoundProducts = [];
      return;
    }
    this.minCharactersError = false;

    if (this.regex.test(searchText)) {
      this.isLoading = true;
      this.debounceService.debounceCall(1000, () => this.getAutocompleteProducts());
    }
  }

  getAutocompleteProducts() {
    const searchText = this.searchForm.controls.searchField.value;
    if (this.regex.test(searchText)) {
      this.productsService.getFoundProducts(searchText).subscribe((result) => {
        this.isLoading = false;
        this.displayFoundProducts = result.tools;
        this.hasResults = Boolean(this.displayFoundProducts.length);
      });
    } else {
      this.isLoading = false;
      this.hasResults = false;
    }
  }

  searchProduct() {
    const searchValue = this.searchForm.controls.searchField.value;

    if (!searchValue || searchValue.length <= 2) {
      this.minCharactersError = true;
      this.searchForm.controls.searchField.setErrors({ invalid: true });
      return;
    }
    this.minCharactersError = false;

    if (!this.isLoading &&!this.displayFoundProducts.length) {
      return;
    }

    this.dataLayerService.logEvent('submit', {
      page_type: 'search',
      search_term: searchValue,
    });
    this.dataLayerService.logEvent('search-event', {
      searchTerm: searchValue,
    });

    if (this.isLoading) {
      this.debounceService.debounceCall(0, () => undefined);
      this.isLoading = false;
      this.closeDropdown();
      this.router.navigate(['/tools/search/', searchValue]);
    } else if (this.displayFoundProducts.length === 1) {
      const first = this.displayFoundProducts[0];
      this.closeDropdown();
      this.productValidationService.validateAndOpen(first);
    } else {
      this.router.navigate(['/tools/search/', searchValue]);
    }
  }

  productSelected(event: MatAutocompleteSelectedEvent) {
    const product = event.option.value;
    if (product) {
      this.dataLayerService.logEvent('click', {
        selected_term: product,
      });
      this.searchForm.controls.searchField.setValue(product.name);
      this.productValidationService.validateAndOpen(product);
    }
  }

  private closeDropdown() {
    this.autocompleteTrigger?.closePanel();
    this.searchInput?.nativeElement.blur();
  }

  clearInput() {
    if (!this.isLoading) {
      this.searchForm.controls.searchField.setValue('');
      this.displayFoundProducts = [];
    }
  }
}
